/**
 * Scheduling & Reschedule API Hooks (Phase 8)
 * Provides React Query hooks for APS Scheduling Engine endpoints:
 * backward/forward scheduling, capacity check, utilization, sequence optimization,
 * setup matrices, hybrid planning, material constraints, cache, and reschedule.
 *
 * Endpoints from AI-Planner Phase 8 (APS Scheduling Engine)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  schedulingApi,
  rescheduleApi,
} from '../api/client';
import type {
  SchedulingRequest,
  SchedulingResult,
  CapacityCheckRequest,
  UtilizationResponse,
  SequenceOptimizeRequest,
  SequenceResult,
  SetupMatrixConfig,
  SetupMatrixResponse,
  HybridPlanningRequest,
  HybridPlanningResult,
  MaterialConstraintsResult,
  SchedulingCacheStatus,
  RescheduleRequest,
  RescheduleResult,
  ConflictSeverity,
} from '../types';

// ============ Query Key Constants ============

export const schedulingQueryKeys = {
  cacheStatus: ['scheduling', 'cache-status'] as const,
  utilization: (params?: Record<string, unknown>) =>
    ['scheduling', 'utilization', params] as const,
};

// ============ Scheduling Mutation Hooks ============

/**
 * Run backward scheduling for operations.
 * POST action — returns scheduling result with operations + conflicts.
 */
export function useBackwardScheduling() {
  const queryClient = useQueryClient();
  return useMutation<SchedulingResult, Error, SchedulingRequest>({
    mutationFn: (data) => schedulingApi.backward(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gantt-data'] });
      queryClient.invalidateQueries({ queryKey: ['capacity-data'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
    },
  });
}

/**
 * Run forward scheduling for operations.
 * POST action — returns scheduling result with operations + conflicts.
 */
export function useForwardScheduling() {
  const queryClient = useQueryClient();
  return useMutation<SchedulingResult, Error, SchedulingRequest>({
    mutationFn: (data) => schedulingApi.forward(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gantt-data'] });
      queryClient.invalidateQueries({ queryKey: ['capacity-data'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
    },
  });
}

/**
 * Run capacity check for selected operations.
 * POST action — returns scheduling result with capacity conflicts.
 */
export function useCapacityCheck() {
  return useMutation<SchedulingResult, Error, CapacityCheckRequest>({
    mutationFn: (data) => schedulingApi.capacityCheck(data),
  });
}

/**
 * Get work center utilization report.
 * POST action — returns utilization per work center with slots.
 */
export function useUtilizationReport() {
  return useMutation<UtilizationResponse, Error, CapacityCheckRequest>({
    mutationFn: (data) => schedulingApi.utilization(data),
  });
}

/**
 * Run sequence optimization for operations.
 * POST action — returns optimized sequences + setup savings.
 */
export function useSequenceOptimize() {
  const queryClient = useQueryClient();
  return useMutation<SequenceResult, Error, SequenceOptimizeRequest>({
    mutationFn: (data) => schedulingApi.sequenceOptimize(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gantt-data'] });
    },
  });
}

/**
 * Load setup matrices into APS engine.
 * POST action — uploads setup matrix configurations.
 */
export function useLoadSetupMatrices() {
  return useMutation<SetupMatrixResponse, Error, SetupMatrixConfig[]>({
    mutationFn: (data) => schedulingApi.setupMatrices(data),
  });
}

/**
 * Run hybrid planning (scheduling + sequence + capacity + material).
 * POST action — returns comprehensive planning result.
 */
export function useHybridPlanning() {
  const queryClient = useQueryClient();
  return useMutation<HybridPlanningResult, Error, HybridPlanningRequest>({
    mutationFn: (data) => schedulingApi.hybrid(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gantt-data'] });
      queryClient.invalidateQueries({ queryKey: ['capacity-data'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
    },
  });
}

/**
 * Check material constraints for operations.
 * POST action — returns shortage/delay analysis.
 */
export function useMaterialConstraints() {
  return useMutation<MaterialConstraintsResult, Error, SchedulingRequest>({
    mutationFn: (data) => schedulingApi.materialConstraints(data),
  });
}

/**
 * Invalidate the scheduling cache.
 * POST action — forces cache rebuild on next scheduling run.
 */
export function useInvalidateSchedulingCache() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, void>({
    mutationFn: () => schedulingApi.invalidateCache(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: schedulingQueryKeys.cacheStatus,
      });
    },
  });
}

// ============ Scheduling Query Hooks ============

/**
 * Get current scheduling cache status.
 * Polls every 30s to show freshness in UI.
 */
export function useSchedulingCacheStatus(enabled = true) {
  return useQuery<SchedulingCacheStatus>({
    queryKey: schedulingQueryKeys.cacheStatus,
    queryFn: () => schedulingApi.getCacheStatus(),
    enabled,
    refetchInterval: 30000,
    staleTime: 15000,
  });
}

// ============ Reschedule Hooks ============

/**
 * Reschedule a single operation (with optional cascade).
 * PATCH action — modifies operation start/end/work center.
 */
export function useRescheduleOperation() {
  const queryClient = useQueryClient();
  return useMutation<
    RescheduleResult,
    Error,
    { operationId: string; data: RescheduleRequest }
  >({
    mutationFn: ({ operationId, data }) =>
      rescheduleApi.reschedule(operationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gantt-data'] });
      queryClient.invalidateQueries({ queryKey: ['capacity-data'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['machine-operations'] });
    },
  });
}

/**
 * Preview a reschedule (dry-run) without applying changes.
 * POST action — returns what-if analysis.
 */
export function useReschedulePreview() {
  return useMutation<
    RescheduleResult,
    Error,
    { operationId: string; data: RescheduleRequest }
  >({
    mutationFn: ({ operationId, data }) =>
      rescheduleApi.preview(operationId, data),
  });
}

// ============ Helper Functions ============

/** Color coding for scheduling conflict severity */
export function getConflictSeverityColor(severity: ConflictSeverity): string {
  switch (severity) {
    case 'error':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'warning':
      return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'info':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

/** Label for scheduling conflict severity */
export function getConflictSeverityLabel(severity: ConflictSeverity): string {
  switch (severity) {
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Info';
    default:
      return severity;
  }
}

/** Color for scheduling result status */
export function getSchedulingStatusColor(status: string): string {
  switch (status) {
    case 'success':
      return 'text-green-600';
    case 'partial':
      return 'text-amber-600';
    case 'failed':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

/** Format computation time for display */
export function formatComputationTime(ms: number): string {
  if (ms < 1000) return `${Number(ms ?? 0).toFixed(1)}ms`;
  return `${(Number(ms ?? 0) / 1000).toFixed(1)}s`;
}
